import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createWorkplace } from 'core/API/workPlace';
import { CreateWorkplaceRequest } from 'types';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import '../GeneralSettings';
import styles from '../GeneralSettings.module.scss';
import { Button } from 'components/ui/button';
import { Autocomplete, Checkbox, TextField, FormControlLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Grid } from '@mui/material';
import { GetTimeZones } from '../../../../../core/API/workplaces';
import { getDays } from '../../../../../core/API/organizationSettings';
import { COUNTRY_LIST, COUNTRY_STATES } from '../constants';
// settingsUtils not needed here after postal code change
import WorkingHoursPicker from 'core/components/WorkingHoursPicker';
import settingsUtils from '../utils';
import { Days } from 'types/Organization';
import { createWorkingHour } from '../../../../../core/API/workingHours';
import dayjs from 'dayjs';
// working hours API imported where needed

// postal code validation handled inline in schema

// Validation schema using Yup
const validationSchema = Yup.object({
	// Required fields
	unit_number: Yup.string()
		.trim()
		.required('Workplace Unit Number is required.')
		.matches(/^[0-9]+$/, 'Workplace Unit Number must contain only digits.')
		.min(1, 'Workplace Unit Number must be at least 1 character.'),
	name: Yup.string().trim().required('Name is required.').min(1, 'Name cannot be empty.'),
	streetAddress: Yup.string()
		.trim()
		.required('Street Address is required')
		.min(1, 'Street Address cannot be empty'),
	city: Yup.string().trim().required('City is required.').min(1, 'City cannot be empty'),
	state: Yup.string().trim().required('State is required.'),
	postcode: Yup.string()
		.trim()
		.required('Postal code is required')
		.test('is-valid-postal-code', 'Invalid postal code format.', function (value) {
			const country = (this.parent as any)?.country;
			return !value ? false : settingsUtils.isValidPostalCode(value, country);
		}),
	country: Yup.string().trim().required('Country is required.'),

	// Time zone (required). Stored as an id (number) in the form, so use mixed() to validate presence.
	timeZone: Yup.mixed().required('Time zone is required.'),

	// Boolean fields (no validation needed as they're controlled)
	closed: Yup.boolean(),

	// Arrays
	working_hours: Yup.array()
		.test(
			'has-open-day',
			'At least one working day is required unless workplace is marked as closed.',
			function (value) {
				const { closed } = this.parent as any;
				if (closed) return true; // if workplace is closed, no working hours required
				if (!Array.isArray(value)) return false;
				return value.some((wh) => wh && wh.closed === false);
			}
		)
		.test(
			'complete-working-hours',
			'All selected working days must have both start and end times.',
			function (value) {
				// const { closed } = this.parent as any;
				// if (closed) return true;
				if (!Array.isArray(value)) return false;
			console.log('validating working hours', value);

			// Check each working hour entry
			for (const wh of value) {
				if (wh && wh.closed === false) {
					// If day is enabled (not closed), both openAt and closeAt are required
					console.log('checking working hour', wh);
					if (!wh.openAt || !wh.closeAt) {
						return this.createError({
							message: 'All selected working days must have both start and end times.',
						});
					}
				}
			}
			return true;
		}
	),	// Date fields
	closedDate: Yup.date()
		.nullable()
		.when('closed', {
			is: true,
			then: (schema) =>
				schema.required('Closed date is required when workplace is marked as closed'),
			otherwise: (schema) => schema.nullable(),
		}),
	openDate: Yup.date().nullable().required('Open date is required'),
});

// Initial form values
const initialValues: any = {
	unit_number: '0001',
	name: '',
	streetAddress: '',
	city: '',
	postcode: '',
	state: '',
	country: 'USA',
	timeZone: '',
	working_hours: [],
	closed: false,
	closedDate: null,
	openDate: dayjs(),
};

interface CreateWorkplaceSettingProps {
	backButtonClick: () => void;
	// nextUnitNumber provided by parent (already computed from workplaces list)
	nextUnitNumber?: string;
}

const CreateWorkplaceSetting: React.FC<CreateWorkplaceSettingProps> = ({
	backButtonClick,
	nextUnitNumber,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const [timeZones, setTimeZones] = useState<Array<any>>([]);
	const [days, setDays] = useState<Days[]>([]);
	const { showToast } = useToastContext();
	// const [oprationUnit, setOprationUnit] = useState<any>();
	// const [workplaceType, setWorkplaceType] = useState<any>();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [zones, daysData] = await Promise.all([GetTimeZones(), getDays()]);
				console.log('timezones', zones);
				setTimeZones(zones || []);
				setDays(daysData || []);
			} catch (error) {
				console.error('Failed to fetch data:', error);
			}
		};
		fetchData();
	}, []);

	const handleSubmitFn = async (values: any, { setSubmitting }: any) => {
		try {
			setApiError(null);
			setApiSuccess(null);

			// Prepare the payload with exact keys as per the provided JSON
			// Ensure unit number is padded with leading zeros to at least 4 digits for saving
			const rawUnitNumber = String(values.unit_number || '').trim();
			const digitsOnly = rawUnitNumber.replace(/\D/g, '');
			const paddedUnitNumber = digitsOnly.padStart(4, '0');
			// Update form value so UI reflects the saved format
			formik.setFieldValue('unit_number', paddedUnitNumber);

			const payload: CreateWorkplaceRequest = {
				name: values.name,
				streetAddress: values.streetAddress,
				city: values.city,
				postcode: values.postcode,
				state: values.state,
				country: values.country,
				timeZone: `/api/time_zones/${values.timeZone}`,
				workplaceUnitNumber: paddedUnitNumber,
				closed: values.closed,
				openDate: values.openDate ? values.openDate.format('YYYY-MM-DD') : null,
				archived: false,
			}; // Call the API
			const response = await createWorkplace(payload);
			console.log('response', response);
			const workplace = response;

			// // Handle working hours updates
			const workingHoursPromises = values.working_hours
				?.filter((wh) => !wh.closed)
				.map(async (wh: any) => {
					const workingHourPayload = {
						workplace: `/api/workplaces/${workplace}`,
						day: wh.day,
						openAt: wh.openAt,
						closeAt: wh.closeAt,
					};
					// If it's a new working hour, create it
					await createWorkingHour(workingHourPayload);
				});

			await Promise.all(workingHoursPromises || []);
			setApiSuccess('Workplace created successfully!');
			showToast('success', 'Workplace created successfully!');
			backButtonClick();
			console.log('API Response:', response);
		} catch (error: any) {
			const errorMessage = error?.message || 'Failed to create workplace';
			setApiError(errorMessage);
			showToast('error', errorMessage);
		} finally {
			setSubmitting(false);
		}
	};

	const handleWorkingHoursChange = (value: any[]) => {
		const formattedWorkingHours = value.map((dayConfig) => ({
			'@id': dayConfig?.['@id'],
			day: dayConfig.day['@id'],
			closed: !dayConfig.enabled,
			openAt: dayConfig.startTime ? dayConfig.startTime.format('HH:mm:ss') : null,
			closeAt: dayConfig.endTime ? dayConfig.endTime.format('HH:mm:ss') : null,
		}));
		formik.setFieldValue('working_hours', formattedWorkingHours);
	};

	const formik = useFormik({
		initialValues,
		validationSchema,
		onSubmit: handleSubmitFn,
		validateOnBlur: true,
		validateOnChange: true,
		validateOnMount: true,
	});

	// Destructure helpers to use in effects without pulling the whole formik object into deps
	const { setFieldValue, values: formikValues } = formik;

	// If parent provided nextUnitNumber, apply it when component mounts or prop changes
	useEffect(() => {
		if (typeof nextUnitNumber === 'string' && nextUnitNumber.length > 0) {
			if (!formikValues.unit_number || formikValues.unit_number === '0001') {
				setFieldValue('unit_number', nextUnitNumber);
			}
		}
		// ensure default country remains USA if not set
		if (!formikValues.country) {
			setFieldValue('country', 'USA');
		}
	}, [nextUnitNumber, setFieldValue, formikValues.unit_number, formikValues.country]);

	// Set default start_of_week to day with position "0" when days are loaded
	useEffect(() => {
		if (days.length > 0 && !formik.values.first_day_of_the_week) {
			const defaultDay = days.find((day) => day.position === '0');
			if (defaultDay) {
				formik.setFieldValue('first_day_of_the_week', defaultDay['@id']);
			}
		}
	}, [days, formik]);

	console.log('formik errors', formik.errors);

	const { isSubmitting, values, errors, touched, handleChange, handleBlur, handleSubmit } = formik;
	return (
		<div className="general-settings-container">
			<>
				<Grid
					container
					spacing={2}
					justifyContent="space-between"
					style={{
						marginBottom: '20px',
					}}
				>
					<Grid item>
						<Button
							style={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: 'white',
								padding: '6px 15px',
								borderRadius: '4px',
								fontSize: '14px',
							}}
							onClick={() => backButtonClick()}
						>
							Back
						</Button>
					</Grid>
					<Grid item>
						<Button
							disabled={isSubmitting}
							onClick={() => handleSubmit()}
							style={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: 'white',
								padding: '6px 15px',
								borderRadius: '4px',
								fontSize: '14px',
							}}
						>
							{isSubmitting ? 'Submitting...' : 'Save'}
						</Button>
					</Grid>
				</Grid>
				<h2 className={styles.formTitle}>Create Workplace</h2>
				{/* API Feedback */}
				{/* {apiSuccess && <div className="success-message">{apiSuccess}</div>}
				{apiError && <div className="error-message">{apiError}</div>} */}
				{formik.touched.working_hours && formik.errors.working_hours && (
					<div
						className="error-message"
						style={{
							color: 'red',
							marginTop: '16px',
							padding: '8px',
							backgroundColor: '#ffe6e6',
							borderRadius: '4px',
						}}
					>
						{formik.errors.working_hours as string}
					</div>
				)}

				<Grid container spacing={2}>
					{/* Workplace Unit Number */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							Workplace Unit Number <span className="required">*</span>
						</label>
						<TextField
							name="unit_number"
							className="form-input"
							required
							fullWidth
							value={values.unit_number}
							onChange={(e) => {
								// Allow only digits while typing
								const digits = String(e.target.value || '').replace(/\D/g, '');
								formik.setFieldValue('unit_number', digits);
							}}
							inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
							onBlur={handleBlur}
							error={touched.unit_number && Boolean(errors.unit_number)}
							helperText={touched.unit_number && (errors.unit_number as string)}
						/>
					</Grid>

					{/* Name */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							Name <span className="required">*</span>
						</label>
						<TextField
							name="name"
							className="form-input"
							required
							fullWidth
							value={values.name}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.name && Boolean(errors.name)}
							helperText={touched.name && (errors.name as string)}
						/>
					</Grid>

					{/* Street Address */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							Street Address <span className="required">*</span>
						</label>
						<TextField
							name="streetAddress"
							className="form-input"
							required
							fullWidth
							value={values.streetAddress}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.streetAddress && Boolean(errors.streetAddress)}
							helperText={touched.streetAddress && (errors.streetAddress as string)}
						/>
					</Grid>

					{/* City */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							City <span className="required">*</span>
						</label>
						<TextField
							name="city"
							className="form-input"
							required
							fullWidth
							value={values.city}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.city && Boolean(errors.city)}
							helperText={touched.city && (errors.city as string)}
						/>
					</Grid>

					{/* Postcode */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							Postal Code <span className="required">*</span>
						</label>
						<TextField
							name="postcode"
							className="form-input"
							required
							fullWidth
							value={values.postcode}
							onChange={handleChange}
							onBlur={handleBlur}
							error={touched.postcode && Boolean(errors.postcode)}
							helperText={touched.postcode && (errors.postcode as string)}
						/>
					</Grid>

					{/* State */}

					<Grid item xs={12} sm={6}>
						<label className="form-label">
							State <span className="required">*</span>
						</label>
						<Autocomplete
							options={COUNTRY_STATES[formik.values.country] || []}
							getOptionLabel={(option) => option}
							value={formik.values.state || ''}
							onChange={(_, value) => formik.setFieldValue('state', value || '')}
							renderInput={(params) => (
								<TextField
									{...params}
									name="state"
									className="form-input"
									required
									fullWidth
									value={values.state}
									onBlur={handleBlur}
									error={touched.state && Boolean(errors.state)}
									helperText={touched.state && (errors.state as string)}
								/>
							)}
						/>
					</Grid>

					{/* Country */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							Country <span className="required">*</span>
						</label>
						<Autocomplete
							options={COUNTRY_LIST}
							getOptionLabel={(option) => option}
							value={formik.values.country || ''}
							onChange={(_, value) => {
								const newCountry = value || '';
								formik.setFieldValue('country', newCountry);
								// If a timezone is selected but its country doesn't match the new country, clear it
								const selectedTz = timeZones.find((tz: any) => tz.id === formik.values.timeZone);
								if (selectedTz && selectedTz.country) {
									if (
										selectedTz.country.toString().toLowerCase() !==
										newCountry.toString().toLowerCase()
									) {
										formik.setFieldValue('timeZone', '');
									}
								}
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									name="country"
									className="form-input"
									required
									fullWidth
									value={values.country}
									error={touched.country && Boolean(errors.country)}
									helperText={touched.country && (errors.country as string)}
								/>
							)}
						/>
					</Grid>

					{/* Time Zone */}
					<Grid item xs={12} sm={6}>
						<label className="form-label">
							Time Zone <span className="required">*</span>
						</label>
						<Autocomplete
							options={
								// If a country is selected, filter zones by that country (case-insensitive).
								formik.values.country
									? timeZones.filter((tz: any) => {
											if (!tz) return false;
											// API may return country in `country` field
											const country = (tz.country || tz.country_name || '').toString();
											return (
												!country ||
												country.toLowerCase() === formik.values.country.toString().toLowerCase()
											);
										})
									: timeZones
							}
							getOptionLabel={(option) =>
								typeof option === 'string' ? option : option.label || option.timeZones || ''
							}
							value={
								timeZones.find((tz) => tz.id === formik.values.timeZone) ||
								(typeof formik.values.timeZone === 'string' ? formik.values.timeZone : null)
							}
							onChange={(_, value) => {
								const timeZoneValue = value?.id;
								formik.setFieldValue('timeZone', timeZoneValue);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									className="form-input"
									required
									fullWidth
									error={touched.timeZone && Boolean(errors.timeZone)}
									helperText={touched.timeZone && (errors.timeZone as string)}
								/>
							)}
						/>
					</Grid>

					{/* Start of Week removed */}

					{/* Open Date */}
					<Grid item xs={12} sm={6} style={{ marginTop: '16px' }}>
						{/* <label className="form-label">
							Open Date <span className="required">*</span>
						</label> */}
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DatePicker
								label="Open Date"
								value={values.openDate}
								onChange={(value) => formik.setFieldValue('openDate', value)}
								slotProps={{
									textField: {
										error: touched.openDate && Boolean(errors.openDate),
										helperText: touched.openDate && (errors.openDate as string),
										fullWidth: true,
										required: true,
										className: 'form-input',
									},
								}}
							/>
						</LocalizationProvider>
					</Grid>

					{/* Working Hours */}
					<Grid item xs={12}>
						<div style={{ marginTop: '10px' }}>
							<WorkingHoursPicker
								value={formik.values.working_hours}
								onChange={handleWorkingHoursChange}
								days={days}
								validateOnChange={true}
							/>
						</div>
					</Grid>

					{/* Closed checkbox and date */}
					<Grid item xs={12}>
						<Grid container alignItems="center" spacing={2}>
							<Grid item xs={3}>
								<FormControlLabel
									control={
										<Checkbox
											checked={values.closed}
											onChange={(e) => {
												formik.setFieldValue('closed', e.target.checked);
												// Clear closedDate if workplace is not closed
												if (!e.target.checked) {
													formik.setFieldValue('closedDate', null);
												}
											}}
											name="closed"
										/>
									}
									label="Closed"
								/>
							</Grid>
							{values.closed && (
								<Grid item xs={9}>
									<LocalizationProvider dateAdapter={AdapterDayjs}>
										<DatePicker
											label="Closed Date"
											value={values.closedDate}
											onChange={(value) => formik.setFieldValue('closedDate', value)}
											format="MM/DD/YYYY"
											slotProps={{
												textField: {
													error: touched.closedDate && Boolean(errors.closedDate),
													helperText: touched.closedDate && (errors.closedDate as string),
													fullWidth: true,
													className: 'form-input',
												},
											}}
										/>
									</LocalizationProvider>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</>
		</div>
	);
};

export default CreateWorkplaceSetting;
